import { DataExchangeRequestDeadlineStateDto } from '../data-transfer/entities/data-exchange-request-entities/data-exchange-request-deadline-state-dto';
import { DataExchangeDeadlineOutcomeDto } from '../data-transfer/entities/data-exchange-request-entities/data-exchange-deadline-outcome-dto';
import { DataExchangeRequestStatusDto } from '../data-transfer/entities/data-exchange-request-entities/data-exchange-request-status-dto';
import { BadgeType } from './badge-type-enum';

interface DeadlineDisplayEntry {
  badgeType: BadgeType | null;
  translationKey: string;
}

const DEADLINE_STATE_DISPLAY: Record<DataExchangeRequestDeadlineStateDto, DeadlineDisplayEntry> = {
  [DataExchangeRequestDeadlineStateDto.None]: { badgeType: null, translationKey: 'None' },
  [DataExchangeRequestDeadlineStateDto.OnTrack]: { badgeType: BadgeType.SUCCESS, translationKey: 'OnTrack' },
  [DataExchangeRequestDeadlineStateDto.CloseToExpiry]: { badgeType: BadgeType.WARNING, translationKey: 'CloseToExpiry' },
  [DataExchangeRequestDeadlineStateDto.Expired]: { badgeType: BadgeType.EXPIRED, translationKey: 'Expired' },
};

const DEADLINE_OUTCOME_DISPLAY: Record<DataExchangeDeadlineOutcomeDto, DeadlineDisplayEntry> = {
  [DataExchangeDeadlineOutcomeDto.None]: { badgeType: null, translationKey: 'None' },
  [DataExchangeDeadlineOutcomeDto.InTime]: { badgeType: BadgeType.SUCCESS, translationKey: 'InTime' },
  [DataExchangeDeadlineOutcomeDto.Late]: { badgeType: BadgeType.INFORMATION, translationKey: 'Late' },
};

const DEADLINE_STATE_FILTER_VALUES: DataExchangeRequestDeadlineStateDto[] = [
  DataExchangeRequestDeadlineStateDto.None,
  DataExchangeRequestDeadlineStateDto.OnTrack,
  DataExchangeRequestDeadlineStateDto.CloseToExpiry,
  DataExchangeRequestDeadlineStateDto.Expired,
];

export interface DeadlineStateDisplay {
  deadlineBadgeType: BadgeType | null;
  deadlineStateLabel: string;
}

export function getDeadlineStateDisplay(deadlineState: DataExchangeRequestDeadlineStateDto | null | undefined): DeadlineStateDisplay {
  const noDeadline = DEADLINE_STATE_DISPLAY[DataExchangeRequestDeadlineStateDto.None];
  const entry = (deadlineState != null ? DEADLINE_STATE_DISPLAY[deadlineState] : undefined) ?? noDeadline;
  return {
    deadlineBadgeType: entry.badgeType,
    deadlineStateLabel: `dataManagement.dataExchangeRequests.deadlineState.${entry.translationKey}`,
  };
}

export function getDeadlineStateFilterOptions(translate: (key: string) => string): { name: string; value: string }[] {
  return DEADLINE_STATE_FILTER_VALUES.map(state => ({
    name: translate(getDeadlineStateDisplay(state).deadlineStateLabel),
    value: DataExchangeRequestDeadlineStateDto[state],
  }));
}

const CLOSING_STATE_FILTER_VALUES: DataExchangeDeadlineOutcomeDto[] = [
  DataExchangeDeadlineOutcomeDto.None,
  DataExchangeDeadlineOutcomeDto.InTime,
  DataExchangeDeadlineOutcomeDto.Late,
];

export function getClosingStateFilterOptions(translate: (key: string) => string): { name: string; value: string }[] {
  return CLOSING_STATE_FILTER_VALUES.map(outcome => ({
    name: translate(
      outcome === DataExchangeDeadlineOutcomeDto.None
        ? getDeadlineStateDisplay(DataExchangeRequestDeadlineStateDto.None).deadlineStateLabel
        : getDeadlineOutcomeDisplay(outcome, 'closingState').deadlineStateLabel
    ),
    value: DataExchangeDeadlineOutcomeDto[outcome],
  }));
}

export function getDeadlineOutcomeDisplay(
  outcome: DataExchangeDeadlineOutcomeDto | null | undefined,
  labelGroup: 'submissionState' | 'closingState'
): DeadlineStateDisplay {
  const noOutcome = DEADLINE_OUTCOME_DISPLAY[DataExchangeDeadlineOutcomeDto.None];
  const entry = (outcome != null ? DEADLINE_OUTCOME_DISPLAY[outcome] : undefined) ?? noOutcome;
  return {
    deadlineBadgeType: entry.badgeType,
    deadlineStateLabel: `dataManagement.dataExchangeRequests.${labelGroup}.${entry.translationKey}`,
  };
}

const CLOSED_STATUSES = [DataExchangeRequestStatusDto.Closed, DataExchangeRequestStatusDto.Deleted];

export function getEffectiveDeadlineDisplay(
  status: DataExchangeRequestStatusDto | null | undefined,
  deadlineState: DataExchangeRequestDeadlineStateDto | null | undefined,
  closingState: DataExchangeDeadlineOutcomeDto | null | undefined
): DeadlineStateDisplay {
  return status != null && CLOSED_STATUSES.includes(status) ? getDeadlineOutcomeDisplay(closingState, 'closingState') : getDeadlineStateDisplay(deadlineState);
}


export interface ReopenBadgeDisplay {
  reopenBadgeType: BadgeType | null;
  reopenBadgeLabel: string;
}

export function getReopenBadgeDisplay(pendingReopenCount: number, isReceived: boolean): ReopenBadgeDisplay {
  const labelKey = isReceived ? 'pending' : 'requested';
  return {
    reopenBadgeType: pendingReopenCount > 0 ? BadgeType.INFORMATION : null,
    reopenBadgeLabel: `dataManagement.dataExchangeRequests.reopen.${labelKey}`,
  };
}
